/*
 * Free Gift for the cart drawer (Concept theme)
 * ------------------------------------------------
 * Two independent free-gift triggers, both handled by this one script so they
 * never race each other over the cart:
 *
 *   1. Spend gift  (window.freeGiftConfig)
 *      Adds the chosen gift once the eligible cart subtotal reaches a threshold,
 *      and removes it if the subtotal drops back below.
 *
 *   2. Product gift (window.productGiftConfig)
 *      Adds the chosen gift whenever a trigger product is in the cart, and
 *      removes it once the trigger product is gone.
 *
 * Both gift lines carry the property `_free_gift='true'` so the store's automatic
 * discount zeroes them. The product gift additionally carries
 * `_free_gift_source='product'` so the two gifts can be told apart. Config is
 * injected by sections/cart-drawer.liquid.
 *
 * Add/remove always re-read the live cart before acting, so a stale line key from
 * an earlier event snapshot can never be sent to /cart/change.js (which would
 * surface Shopify's "no valid id or line parameter" alert).
 *
 * A gift can also be removed manually from the cart. If a shopper removes one
 * while it still qualifies, we remember that (session dismissal) and stop
 * re-adding it, so it doesn't feel unremovable. Losing the qualification (subtotal
 * dropping below the threshold, or the trigger product leaving the cart) clears
 * the dismissal, so qualifying again re-offers the gift.
 *
 * At most one add/remove runs per reconcile pass; each mutation republishes the
 * cart, which re-runs reconcile, so the second gift is handled on the next pass.
 *
 * CHOICE MODE (either trigger, `chooseFromCollection`)
 * ---------------------------------------------------
 * Instead of one fixed gift, the cart drawer renders a picker built from a
 * collection (snippets/free-gift-picker.liquid) and the shopper claims a gift by
 * clicking it. Nothing is ever added automatically, so there is no dismissal to
 * track: removing a claimed gift just re-renders the picker. Removal when the
 * trigger is lost still applies, exactly as in fixed-gift mode.
 */
(function () {
  'use strict';

  var GIFT_PROPERTY = '_free_gift';
  var SOURCE_PROPERTY = '_free_gift_source';
  var PRODUCT_SOURCE = 'product';
  var SPEND_DISMISS_KEY = 'freeGiftDismissed';
  var PRODUCT_DISMISS_KEY = 'productGiftDismissed';
  var busy = false;
  var pending = false;
  var lastSpendGiftPresent = false;
  var lastProductGiftPresent = false;

  function isDismissed(key) {
    try { return sessionStorage.getItem(key) === '1'; } catch (e) { return false; }
  }
  function setDismissed(key, value) {
    try {
      if (value) sessionStorage.setItem(key, '1');
      else sessionStorage.removeItem(key);
    } catch (e) { /* sessionStorage unavailable */ }
  }

  function ready() {
    return window.theme && theme.pubsub && theme.routes && theme.utils;
  }

  function getSpendConfig() {
    var c = window.freeGiftConfig;
    if (!c || !c.enabled || !c.threshold) return null;
    // Choice mode has no single variant: the picker supplies one per click.
    if (!c.chooseFromCollection && !c.variantId) return null;
    return c;
  }

  function getProductConfig() {
    var c = window.productGiftConfig;
    if (!c || !c.enabled || !c.triggerProductId) return null;
    if (!c.chooseFromCollection && !c.giftVariantId) return null;
    return c;
  }

  function itemProps(item) {
    return item.properties || {};
  }
  function isGiftLine(item) {
    var v = itemProps(item)[GIFT_PROPERTY];
    return v === 'true' || v === true;
  }
  function isProductGiftLine(item) {
    return isGiftLine(item) && itemProps(item)[SOURCE_PROPERTY] === PRODUCT_SOURCE;
  }

  // The spend gift line: a gift line that is NOT the product-triggered one.
  function findSpendGiftLine(cart) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (isGiftLine(cart.items[i]) && !isProductGiftLine(cart.items[i])) return cart.items[i];
    }
    return null;
  }

  function findProductGiftLine(cart) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (isProductGiftLine(cart.items[i])) return cart.items[i];
    }
    return null;
  }

  // Whether the trigger product (any variant) is in the cart, ignoring gift lines.
  function hasTriggerProduct(cart, config) {
    if (!cart || !cart.items) return false;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (isGiftLine(item)) continue;
      if (item.product_id === config.triggerProductId) return true;
    }
    return false;
  }

  // Spend that counts toward the spend threshold, excluding the gift's own price.
  function eligibleTotal(cart) {
    var giftLine = findSpendGiftLine(cart);
    var giftPrice = giftLine ? giftLine.final_line_price : 0;
    return cart.total_price - giftPrice;
  }

  // Collect the cart section ids the theme wants re-rendered (cart drawer, main cart).
  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function getCart() {
    return fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET')).then(function (r) {
      return r.json();
    });
  }

  function publishCart(cart) {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift', cart: cart });
  }

  // Something went wrong (e.g. a line vanished under us) - resync the drawer UI
  // from the server instead of publishing an error cart (which would alert).
  function resync() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: {} }));
  }

  // Add a gift variant to the cart. `extraProps` lets the product gift tag its
  // line so it can be distinguished from the spend gift.
  function addGift(variantId, extraProps) {
    busy = true;

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('properties[' + GIFT_PROPERTY + ']', 'true');
    if (extraProps) {
      for (var key in extraProps) {
        if (Object.prototype.hasOwnProperty.call(extraProps, key)) {
          formData.append('properties[' + key + ']', extraProps[key]);
        }
      }
    }
    formData.append('sections', bundledSections());
    formData.append('sections_url', window.location.pathname);

    var fetchConfig = theme.utils.fetchConfig('javascript');
    fetchConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete fetchConfig.headers['Content-Type'];
    fetchConfig.body = formData;

    return fetch(theme.routes.cart_add_url, fetchConfig)
      .then(function (response) { return response.json(); })
      .then(function (added) {
        // added.status is set when the add failed (e.g. gift out of stock).
        if (added.status) return null;
        return getCart().then(function (cart) {
          cart.sections = added.sections;
          publishCart(cart);
        });
      })
      .catch(function (error) { console.warn('[free-gift] add failed', error); })
      .finally(settle);
  }

  // Remove a gift line. `keepIf(cart)` is re-checked against the live cart: if it
  // returns true the gift still qualifies and is kept (guards against races with
  // the theme's own cart updates).
  function removeGift(findLine, keepIf) {
    busy = true;

    getCart()
      .then(function (cart) {
        var giftLine = findLine(cart);
        if (!giftLine || !giftLine.key) return null;   // already gone
        if (keepIf(cart)) return null;                 // still qualifies; keep it

        var body = JSON.stringify({
          id: giftLine.key,
          quantity: 0,
          sections: bundledSections()
        });

        return fetch(theme.routes.cart_change_url, Object.assign({}, theme.utils.fetchConfig(), { body: body }))
          .then(function (response) { return response.json(); })
          .then(function (result) {
            if (result.status || result.errors) {
              resync();
              return;
            }
            publishCart(result);
          });
      })
      .catch(function (error) { console.warn('[free-gift] remove failed', error); })
      .finally(settle);
  }

  // Called when an add/remove finishes. If the cart changed while we were busy,
  // re-check the live state once so we never get stuck mid-transition.
  function settle() {
    busy = false;
    if (pending) {
      pending = false;
      getCart().then(reconcile).catch(function () {});
    }
  }

  // Spend gift: add/remove based on the eligible subtotal. Returns true if it
  // started a mutation (so the caller stops and lets the republish re-run us).
  function reconcileSpendGift(cart) {
    var config = getSpendConfig();
    if (!config) return false;

    var giftPresent = !!findSpendGiftLine(cart);
    var wasPresent = lastSpendGiftPresent;
    lastSpendGiftPresent = giftPresent;
    var eligible = eligibleTotal(cart);

    if (eligible < config.threshold) {
      // Below the threshold: re-offer the gift next time, and pull it if present.
      if (isDismissed(SPEND_DISMISS_KEY)) setDismissed(SPEND_DISMISS_KEY, false);
      if (giftPresent) {
        removeGift(findSpendGiftLine, function (c) { return eligibleTotal(c) >= config.threshold; });
        return true;
      }
      return false;
    }

    // At or above the threshold.
    if (giftPresent) return false;

    // Choice mode: the cart drawer shows a picker and the shopper claims a gift
    // themselves. Never add on their behalf, and never dismiss - a removed gift
    // simply brings the picker back.
    if (config.chooseFromCollection) return false;

    // Gift is gone while still qualifying. If it was just here, the shopper
    // removed it themselves - respect that instead of re-adding.
    if (wasPresent) {
      setDismissed(SPEND_DISMISS_KEY, true);
      return false;
    }
    if (isDismissed(SPEND_DISMISS_KEY)) return false;
    addGift(config.variantId);
    return true;
  }

  // Product gift: add/remove based on whether the trigger product is in the cart.
  // Returns true if it started a mutation.
  function reconcileProductGift(cart) {
    var config = getProductConfig();
    if (!config) return false;

    var giftPresent = !!findProductGiftLine(cart);
    var wasPresent = lastProductGiftPresent;
    lastProductGiftPresent = giftPresent;
    var triggerPresent = hasTriggerProduct(cart, config);

    if (!triggerPresent) {
      // Trigger gone: re-offer next time, and pull the gift if present.
      if (isDismissed(PRODUCT_DISMISS_KEY)) setDismissed(PRODUCT_DISMISS_KEY, false);
      if (giftPresent) {
        removeGift(findProductGiftLine, function (c) { return hasTriggerProduct(c, config); });
        return true;
      }
      return false;
    }

    // Trigger is present.
    if (giftPresent) return false;

    // Choice mode: the picker offers the gifts; see reconcileSpendGift.
    if (config.chooseFromCollection) return false;

    // Gift is gone while the trigger is still here. If it was just present, the
    // shopper removed it themselves - respect that instead of re-adding.
    if (wasPresent) {
      setDismissed(PRODUCT_DISMISS_KEY, true);
      return false;
    }
    if (isDismissed(PRODUCT_DISMISS_KEY)) return false;
    addGift(config.giftVariantId, buildProductGiftProps());
    return true;
  }

  function buildProductGiftProps() {
    var props = {};
    props[SOURCE_PROPERTY] = PRODUCT_SOURCE;
    return props;
  }

  // Decide whether either gift should be added or removed, and act on one per
  // pass. Each mutation republishes the cart, which re-runs reconcile for the
  // other gift.
  function reconcile(cart) {
    if (!cart || cart.errors) return;
    if (busy) { pending = true; return; }

    if (reconcileSpendGift(cart)) return;
    reconcileProductGift(cart);
  }

  // Choice mode: claim a gift from the picker rendered by the cart drawer. The
  // picker is re-rendered with every cart update, so the listener is delegated.
  function onPickerClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var button = target.closest('[data-free-gift-choose]');
    if (!button || button.disabled) return;

    var picker = button.closest('[data-free-gift-picker]');
    if (!picker) return;

    var isProductGift = picker.getAttribute('data-free-gift-source') === PRODUCT_SOURCE;
    var config = isProductGift ? getProductConfig() : getSpendConfig();
    if (!config || !config.chooseFromCollection) return;

    // A multi-variant gift carries a select; a single-variant one is pinned on
    // the button itself.
    var card = button.closest('.free-gift-picker__item');
    var select = card ? card.querySelector('[data-free-gift-variant]') : null;
    var variantId = select ? select.value : button.getAttribute('data-variant-id');
    if (!variantId) return;

    event.preventDefault();
    if (busy) return;

    // Lock the whole picker so a double tap can't claim two gifts.
    var buttons = picker.querySelectorAll('[data-free-gift-choose]');
    var i;
    for (i = 0; i < buttons.length; i++) buttons[i].disabled = true;

    addGift(variantId, isProductGift ? buildProductGiftProps() : null).then(function () {
      // On success the drawer re-renders without the picker, so this only matters
      // when the add failed (e.g. the gift sold out between render and click).
      for (i = 0; i < buttons.length; i++) buttons[i].disabled = false;
    });
  }

  // ---- carousel -----------------------------------------------------------
  // The gift strip scrolls horizontally. Arrows are hidden unless it actually
  // overflows, and disable at each end.

  function carouselParts(carousel) {
    if (!carousel) return null;
    var list = carousel.querySelector('[data-free-gift-list]');
    if (!list) return null;
    return {
      list: list,
      prev: carousel.querySelector('[data-free-gift-prev]'),
      next: carousel.querySelector('[data-free-gift-next]')
    };
  }

  function updateCarousel(carousel) {
    var parts = carouselParts(carousel);
    if (!parts) return;

    var maxScroll = parts.list.scrollWidth - parts.list.clientWidth;
    var overflows = maxScroll > 1;

    carousel.classList.toggle('free-gift-picker--scrollable', overflows);
    if (parts.prev) parts.prev.disabled = !overflows || parts.list.scrollLeft <= 1;
    if (parts.next) parts.next.disabled = !overflows || parts.list.scrollLeft >= maxScroll - 1;
  }

  // The drawer is rendered while hidden, so widths are only real once it opens.
  // A ResizeObserver catches that moment; scroll and resize keep it honest after.
  var carouselResize = typeof ResizeObserver === 'function'
    ? new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          updateCarousel(entries[i].target.closest('[data-free-gift-carousel]'));
        }
      })
    : null;

  function refreshCarousels() {
    var carousels = document.querySelectorAll('[data-free-gift-carousel]');
    for (var i = 0; i < carousels.length; i++) {
      updateCarousel(carousels[i]);
      if (carouselResize) {
        var list = carousels[i].querySelector('[data-free-gift-list]');
        if (list) carouselResize.observe(list);
      }
    }
  }

  function onCarouselClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var button = target.closest('[data-free-gift-prev], [data-free-gift-next]');
    if (!button || button.disabled) return;

    var parts = carouselParts(button.closest('[data-free-gift-carousel]'));
    if (!parts) return;

    event.preventDefault();

    // Scroll by one card, falling back to most of a screenful.
    var card = parts.list.querySelector('.free-gift-picker__item');
    var gap = 10;
    var step = card ? card.offsetWidth + gap : parts.list.clientWidth * 0.8;
    var direction = button.hasAttribute('data-free-gift-prev') ? -1 : 1;

    parts.list.scrollLeft += step * direction;
  }

  function init() {
    if (!ready() || (!getSpendConfig() && !getProductConfig())) return;

    document.addEventListener('click', onPickerClick);
    document.addEventListener('click', onCarouselClick);
    // scroll does not bubble, so listen on the capture phase.
    document.addEventListener('scroll', function (event) {
      var el = event.target;
      if (el && el.nodeType === 1 && el.hasAttribute('data-free-gift-list')) {
        updateCarousel(el.closest('[data-free-gift-carousel]'));
      }
    }, true);
    window.addEventListener('resize', refreshCarousels);

    // React to every cart mutation the theme publishes.
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
      reconcile(event.cart);
      // The drawer just re-rendered, so the picker is a fresh element.
      refreshCarousels();
    });

    // Run once on load against the current cart state.
    refreshCarousels();
    getCart().then(reconcile).catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
